import { replaceJSXTokens } from '@tm/client-form/src/misc/replaceJSXTokens'
import { PublicConsentField } from '@tm/shared-lib/src/field'
import { useEffect, useMemo } from 'react'
import { useI18N } from '../../hooks/useI18n'
import { InProgressState } from '../../hooks/useSurvey/states/inProgress'
import { State } from '../../hooks/useSurvey/states/state'
import { useSurveyState } from '../../hooks/useSurveyState'
import { useSurveyValue } from '../../hooks/useSurveyValue'
import { Checkbox } from '../field/Checkbox'
import { getPrivacyPolicyAndOrganization } from './PrivacyPolicy'

const trustmaryPolicyUrl = 'https://trustmary.com/terms-summary-for-reviewers/'

interface Props {
  field: PublicConsentField
}

const shouldAskConsent = (state: State) => (state as InProgressState).publicConsent === 'gdpr-compliant'

const PrivacyPolicyLink = ({ label, url }: { url: string | undefined; label: string }) =>
  url ? (
    <a href={url} target="_blank" rel="noreferrer">
      {label}
    </a>
  ) : (
    <>{label}</>
  )

export const ConsentText = ({
  label,
  privacyPolicyOrganization,
  privacyPolicyUrl,
  trustmaryPolicyUrl,
}: {
  label: string
  privacyPolicyOrganization: string
  privacyPolicyUrl: string | undefined
  trustmaryPolicyUrl: string
}) => {
  const { t } = useI18N()

  return (
    <div>
      {replaceJSXTokens(label, {
        organizationPrivacyPolicy: (
          <PrivacyPolicyLink
            label={t('publicConsent.v2.policyLinks.organizationPolicy').replace(
              '{organizationName}',
              privacyPolicyOrganization
            )}
            url={privacyPolicyUrl}
          />
        ),
        trustmaryPrivacyPolicy: (
          <PrivacyPolicyLink label={t('publicConsent.v2.policyLinks.trustmaryPolicy')} url={trustmaryPolicyUrl} />
        ),
      })}
    </div>
  )
}

export function PublicConsentQuestion({ field }: Props): JSX.Element {
  const { t } = useI18N()
  const [value, setValue] = useSurveyValue(field)
  const [state] = useSurveyState()

  const { privacyPolicy, privacyPolicyOrganization } = getPrivacyPolicyAndOrganization(state)

  const booleanValue = useMemo(() => {
    if (typeof value === 'string' && (value === 'true' || value === 'false' || value === '')) {
      return value === 'true'
    } else if (value === undefined || typeof value === 'boolean') {
      return value
    }
    return undefined
  }, [value])

  // No need to request consent if non-GDPR compliant consent is activated so just set this box enabled
  useEffect(() => {
    // Editor will not give the in progress state
    if (state.type !== 'in progress') return

    if (!shouldAskConsent(state)) setValue(true)
  }, [setValue, state])

  // Only render text as checkbox is now not needed

  if (!shouldAskConsent(state))
    return (
      <div data-testid="label">
        <ConsentText
          label={t('publicConsent.v2.nonGdpr.label')}
          trustmaryPolicyUrl={trustmaryPolicyUrl}
          privacyPolicyOrganization={privacyPolicyOrganization}
          privacyPolicyUrl={privacyPolicy}
        />
      </div>
    )

  // Render checkbox for confirmation

  return (
    <Checkbox
      value={booleanValue}
      onValueChange={setValue}
      renderLabel={() => (
        <ConsentText
          label={t('publicConsent.v2.gdpr.label')}
          trustmaryPolicyUrl={trustmaryPolicyUrl}
          privacyPolicyOrganization={privacyPolicyOrganization}
          privacyPolicyUrl={privacyPolicy}
        />
      )}
    />
  )
}
